<script>
import { mapActions, mapState } from "vuex";
import NavigationWrapper from "@/components/NavigationWrapper";
import MultiSelect from "@/components/MultiSelect";
import HashrateInput from "@/components/HashrateInput";
import { defaultHandle } from "@/utils";
import InfoTooltip from "@/components/InfoTooltip";

export default {
  components: {
    NavigationWrapper,
    MultiSelect,
    HashrateInput,
    InfoTooltip,
  },

  data() {
    return {
      // The alert being edited, if any
      alert: null,

      // The hashrate threshold
      hashrateThreshold: 0,

      // A list of selected account objects
      selectedAccounts: [],

      // The group ID that the alert should be a part of
      groupId: null,

      // The name of the alert
      name: "",

      // The handle of the alert
      handle: "",

      // The alert's kind
      kind: "HASHRATE_FALLS_BELOW",

      // Notes for the alert
      notes: "",

      // Whether or not the alert is enabled (not editable here)
      enabled: true,

      // Whether or not notify on resolve is enabled
      notifyOnResolve: true,

      // Delay between repeat notifications
      notifyRepeatDelay: null,

      // Whether notification repetition is enabled
      notifyRepeat: false,

      // Time threshold for alert
      timeThreshold: 300,

      // Whether to notify when list of affected workers changes (only
      // relevant for per-worker alerts)
      notifyOnChange: true,
    };
  },

  computed: {
    // Retrieve team members, alerts, and groups from the vuex state
    ...mapState(["teamMembers", "alerts", "groups"]),

    // Compute the account IDs (for API) from the selected accounts
    accountIds() {
      return this.selectedAccounts.map((account) => account.id);
    },

    // Format the formdata for the API
    formData() {
      return {
        groupId: this.groupId,
        accountIds: this.accountIds,
        name: this.name,
        handle: this.handle,
        kind: this.kind,
        notes: this.notes,
        enabled: this.enabled,
        notifyOnResolve: this.notifyOnResolve,
        notifyRepeatDelay: this.notifyRepeat ? this.notifyRepeatDelay : null,
        timeThreshold: this.timeThreshold,
        notifyOnChange: this.notifyOnChange,
        hashrateThreshold: this.hashrateThreshold,
      };
    },

    // Format the options for the grop selector
    groupOptions() {
      return Object.values(this.groups).map((group) => {
        return {
          value: group.id,
          text: group.name,
        };
      });
    },

    // Whether the component is used for editing an alert
    // as opposed to creating a new one.
    editing() {
      return this.alertId != null;
    },
  },

  watch: {
    // When the name field changes, adjust the handle to match it
    name(value, old) {
      this.adjustHandle(value, old);
    },
  },

  // When the component is created, fetch the groups and team members,
  // and (if editing) the alert in question. If editing, also populate
  // the form fields with the appropriate data.
  async created() {
    // Get the alert ID from the URL
    this.alertId = this.$route.params.alertId;

    // Fetch the groups, team members, and if editing, the alert to edit
    let fetches = [
      this.getGroups(),
      this.getTeamMembers([
        "account.id,account.name,account.handle,account.email,role.name",
      ]),
    ];
    if (this.editing) {
      fetches.push(this.getAlert({ alertId: this.alertId }));
    }
    await Promise.all(fetches);

    // If editing, populate the form fields
    if (this.editing) {
      this.alert = this.alerts[this.alertId];

      this.groupId = this.alert.group_id;
      this.name = this.alert.name;
      this.handle = this.alert.handle;
      this.kind = this.alert.kind;
      this.notes = this.alert.notes;
      this.selectedAccounts = [...this.alert.accounts];
      this.hashrateThreshold = this.alert.hashrate_threshold;
      this.enabled = this.alert.enabled;
      this.notifyOnResolve = this.alert.notify_on_resolve;
      this.notifyRepeatDelay = this.alert.notify_repeat_delay;
      this.notifyRepeat = this.alert.notify_repeat_delay !== null;
      this.timeThreshold = this.alert.time_threshold;
      this.notifyOnChange = this.alert.notify_on_change;
    }
  },

  methods: {
    // On submit, put or post depending on whether editing or creating
    onSubmit() {
      if (this.editing) {
        this.put();
      } else {
        this.post();
      }
    },

    // Post the current form data as a new alert to the API
    async post() {
      // Create worker given the form inputs
      const postSuccess = await this.postAlert(this.formData);

      // If created successfully, then redirect
      if (postSuccess) {
        await this.$router.replace("/alerts");
      }
    },

    // Put the current form data as an update to the API
    async put() {
      // Update worker given the form inputs
      const putSuccess = await this.putAlert({
        alertId: this.alertId,
        ...this.formData,
      });

      // If updated successfully, then redirect
      if (putSuccess) {
        await this.$router.replace(`/alerts/${this.alertId}`);
      }
    },

    // Update the handle to match the name, unless the
    // handle has been specifically edited to be different
    adjustHandle(value, old) {
      if (this.handle === "" || this.handle === defaultHandle(old)) {
        this.handle = defaultHandle(value);
      }
    },

    // Determine whether a member matches a search term
    alerteeMatchesSearch(search, member) {
      search = search.trim().toLowerCase();
      let full_name = `${member.first_name} ${member.last_name}`;
      return (
        full_name.toLowerCase().includes(search) ||
        member.email.toLowerCase().includes(search) ||
        member.handle.toLowerCase().includes(search)
      );
    },

    // Other actions mapped from the vuex store
    ...mapActions([
      "getGroups",
      "postAlert",
      "putAlert",
      "getTeamMembers",
      "getAlert",
    ]),
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/alerts">Alerts</b-breadcrumb-item>
      <b-breadcrumb-item v-if="alert && editing" :to="`/alerts/${alertId}`">
        {{ alert.name }}
      </b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" active>Edit</b-breadcrumb-item>
      <b-breadcrumb-item v-else active>Add Alert</b-breadcrumb-item>
    </template>
    <b-row>
      <b-col>
        <h1>{{ editing ? "Edit" : "Add" }} Alert</h1>
        <p>A configured alert will send an email whenever it is triggered.</p>
        <b-card>
          <b-card-text>
            <b-form @submit.stop.prevent="onSubmit">
              <b-row>
                <b-col cols="12" sm="6">
                  <b-form-group label="Name" label-for="name">
                    <b-form-input
                      required
                      v-model="name"
                      id="name"
                      placeholder="Name"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      A human-readable name for this alert.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" sm="6">
                  <b-form-group label="Handle" label-for="handle">
                    <input
                      required
                      v-model="handle"
                      id="handle"
                      placeholder="Handle"
                      pattern="[a-z,0-9]([a-z,0-9]|(-[a-z,0-9]))*"
                      minlength="3"
                      maxlength="64"
                      autocomplete="off"
                      class="form-control"
                    />
                    <small class="form-text text-muted">
                      A unique identifier for this alert. Must be a
                      hyphen-separated series of lowercase words, e.g.
                      deploy1-hashrate-low.
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" sm="6">
                  <b-form-group label="Kind" label-for="kind">
                    <b-form-select
                      required
                      v-model="kind"
                      id="kind"
                      :options="[
                        {
                          text: 'Group hashrate falls below threshold',
                          value: 'HASHRATE_FALLS_BELOW',
                        },
                        {
                          text: 'Any worker\'s hashrate falls below threshold',
                          value: 'WORKER_HASHRATE_FALLS_BELOW',
                        },
                      ]"
                    ></b-form-select>
                    <small class="form-text text-muted">
                      What kind of conditions should cause this alert to
                      trigger.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" sm="6">
                  <b-form-group label="Group" label-for="groupId">
                    <b-form-select
                      required
                      v-model="groupId"
                      id="groupId"
                      :options="groupOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>Group</option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      The group that will be monitored by this alert.
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" sm="6">
                  <b-form-group label="Hashrate Threshold" label-for="groupId">
                    <HashrateInput v-model="hashrateThreshold" />
                    <small class="form-text text-muted">
                      Hashrate threshold that will cause this alert to trigger.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" sm="6">
                  <b-form-group
                    label="Time Threshold"
                    label-for="timeThreshold"
                  >
                    <b-form-select
                      required
                      v-model="timeThreshold"
                      id="timeThreshold"
                      :options="[
                        { value: 300, text: 'Immediate' },
                        { value: 900, text: 'After 15 minutes' },
                        { value: 1800, text: 'After 30 minutes' },
                        { value: 3600, text: 'After 1 hour' },
                        { value: 86400, text: 'After 24 hours' },
                      ]"
                    ></b-form-select>
                    <small class="form-text text-muted">
                      How long the other conditions must be met before
                      triggering the alert.
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group>
                <b-form-checkbox
                  v-model="notifyOnChange"
                  v-if="kind == 'WORKER_HASHRATE_FALLS_BELOW'"
                >
                  Notify when additional workers fall below threshold
                  <InfoTooltip
                    text="If enabled, send a notification when additional workers fall below the threshold, even if the alert is already triggering"
                  />
                </b-form-checkbox>
              </b-form-group>
              <b-form-group>
                <b-form-checkbox v-model="notifyOnResolve">
                  Notify when this alert is resolved
                </b-form-checkbox>
              </b-form-group>
              <b-form-group>
                <b-form-checkbox v-model="notifyRepeat">
                  Send reminders until alert is resolved
                </b-form-checkbox>
              </b-form-group>
              <b-form-group
                v-if="notifyRepeat"
                label="Reminder Frequency"
                label-for="notifyRepeatDelay"
              >
                <b-form-select
                  required
                  v-model="notifyRepeatDelay"
                  id="notifyRepeatDelay"
                  :options="[
                    { value: 300, text: 'Every 5 minutes' },
                    { value: 600, text: 'Every 10 minutes' },
                    { value: 900, text: 'Every 15 minutes' },
                    { value: 1800, text: 'Every 30 minutes' },
                    { value: 3600, text: 'Every hour' },
                    { value: 86400, text: 'Every 24 hours' },
                  ]"
                >
                  <template #first>
                    <option :value="null" disabled>Reminder Frequency</option>
                  </template>
                </b-form-select>
                <small class="form-text text-muted">
                  How often to send reminder emails while the alert is
                  triggered.
                </small>
              </b-form-group>
              <b-form-group label="Notes" label-for="notes">
                <b-form-input
                  v-model="notes"
                  id="name"
                  placeholder="Notes"
                ></b-form-input>
              </b-form-group>
              <b-form-group
                id="destination-worker-name-group"
                :label="`Alertees (${selectedAccounts.length})`"
                label-for="alertees"
              >
                <MultiSelect
                  primary-key="id"
                  v-model="selectedAccounts"
                  :options="teamMembers"
                  :match="alerteeMatchesSearch"
                >
                  <template #format="{ option }">
                    <span v-if="option.first_name || option.last_name">
                      {{ option.first_name }}
                      {{ option.last_name }}
                    </span>
                    <span v-else>
                      {{ option.handle }}
                    </span>
                    <span class="text-muted">({{ option.email }})</span>
                  </template>
                </MultiSelect>
                <small class="form-text text-muted">
                  People who will receive an email when this alert is triggered.
                </small>
              </b-form-group>
              <b-button type="submit" variant="primary">Save Alert</b-button>
            </b-form>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>
